import React, { memo, ReactElement, useEffect, useRef, useState } from 'react';
import { Collapse, SelectChangeEvent } from '@mui/material';
import { AxiosError } from 'axios';
import { useTeamsUsersByTeamId } from '../../../../lib/api/swrApi';
import styles from './TeamAccordionItem.module.scss';
import PureHeading from '../../../common/Heading/PureHeading';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import { AccordionPlusIcon } from '../../../../lib/icons/AccordionPlusIcon';
import { AccordionMinusIcon } from '../../../../lib/icons/AccordionMinusIcon';
import Table from '../Table/Table';
import useBackendTable from '../../../../lib/hooks/useBackendTable';
import PureTable from '../../../common/PureTable/PureTable';
import { useConfig } from '../../../../lib/hooks/config';
import PureLink from '../../../common/Link/PureLink';
import { TABLE_LABELS, TEAM_MANAGMENT_LABELS, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import { Label } from '../../../common/Label/Label';
import Badge from '../../../common/Badge/Badge';
import WithDevice from '../../../common/WithDevice/WithDevice';
import { ConfirmDialog } from '../../../common/ConfirmDialog/ConfirmDialog';
import { useSnackbarContext } from '../../../../lib/context/SnackbarContext';
import { NotificationType } from '../Notification/Notification';
import { teamAccordionItemIds } from '../../../../lib/tests/testIds';
import { Path } from '../../../../lib/types/helperTypes';
import { classes } from '../../../../lib/utils/cssUtils';
import { useDeleteTeamUserById } from '../../../../lib/api/axiosApi';
import { ID_HASH_PARAM, TEAM_ACCORDION_MEMBERS_HEADINGS } from '../../../../lib/constants/courses';
import { isNil } from '../../../../lib/utils/isNil';
import useDebouncedCallback from '../../../../lib/hooks/useDebouncedCallback';
import { SEARCH_DEBOUNCE_TIME } from '../../../../lib/constants/globals';
import ErrorInfo from '../ErrorInfo/ErrorInfo';
import useDeleteState from '../../../../lib/hooks/useDeleteState';
import { useTeamMembersContext } from '../../../../lib/context/TeamMembersContext';
import { TeamAccordionItemProps } from './TeamAccordionItem.types';
import TeamAccordionItemActions from './TeamAccordionItemActions/TeamAccordionItemActions';
import { Api } from '../../../../lib/types/apiSpec';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';
import { isCancelError } from '../../../../lib/utils/errorMessages.utils';

const getSortValues = (sortBy: string[]) =>
  sortBy?.flatMap(item => [
    {
      value: `${item}|asc`,
      labelKey: TABLE_LABELS.SORT_BY_ITEM(item),
    },
    {
      value: `${item}|desc`,
      labelKey: TABLE_LABELS.SORT_BY_ITEM(item),
    },
  ]);

const getTableData = (
  users: Api.TeamUserAndSummary[],
  teamMemberDetailsPage: string,
  handleOpenDialog: (userid: string) => void,
  itemsAwaitingDelete: { [userid: string]: boolean },
) => {
  if (!users?.length) {
    return {};
  }

  return users.reduce<Record<string, (string | ReactElement)[]>>((prev, curr) => {
    const id = `${curr.userId}`;
    const idHash = `${ID_HASH_PARAM}=${id}`;

    return {
      ...prev,
      [id]: [
        <PureLink key={`name-${id}`} className={styles.nameLink} href={`${teamMemberDetailsPage}#${idHash}`}>
          {`${curr.firstName} ${curr.lastName ?? ''}`}
        </PureLink>,
        `${curr.email ?? ''}`,
        `${curr.totalAssigned ?? ''}`,
        `${curr.totalCompleted ?? ''}`,
        !itemsAwaitingDelete[id] ? (
          <PureTable.DeleteAction key={`delete-${id}`} id={id} titleKey="delete.member" onClick={handleOpenDialog} />
        ) : (
          <PureTable.Loader key={`delete-loader-${id}`} />
        ),
      ],
    };
  }, {});
};

const TeamAccordionItem = memo<TeamAccordionItemProps>(
  ({ teamMeta, membersCount, overdue, className, isExpanded }: TeamAccordionItemProps) => {
    const previousCall = useRef<AbortController | undefined>(undefined);
    const { removeMemberSuccessMessage, onClick, refreshTeamsSummary } = useTeamMembersContext();
    const { teamMemberDetailsPage = '#' } = useConfig() ?? {};
    const { triggerSnackbar } = useSnackbarContext();
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [onDeleteConfirm, setOnDeleteConfirm] = useState<() => void>(() => null);
    const [localPhrase, setLocalPhrase] = useState('');
    const [itemsAwaitingDelete, itemsAwaitingDeleteDispatch] = useDeleteState({});
    const isError = useRef(false);
    const deleteTeamUserById = useDeleteTeamUserById();
    const [tableApiProps, dispatch] = useBackendTable<Path<'/teams/{teamid}/users'>>({
      pageSize: 10,
      page: 1,
      sortBy: Api.SortBy.name,
      sortDirection: Api.SortDirection.asc,
      search: localPhrase,
    });
    const { triggerSnackbarError } = useErrorMapping();

    const getSignal = () => {
      if (previousCall.current) {
        previousCall.current.abort();
      }
      previousCall.current = new AbortController();

      return previousCall.current.signal;
    };

    const {
      data,
      mutate: refreshMembersList,
      isValidating,
      error,
    } = useTeamsUsersByTeamId(teamMeta.id, tableApiProps, getSignal, isExpanded);

    const headerId = `team-accordion-header-${teamMeta.id}`;
    const contentId = `team-accordion-content-${teamMeta.id}`;
    const activeSortValue = `${tableApiProps.sortBy}|${tableApiProps.sortDirection}`;
    const totalPages =
      data?.Meta?.totalPages ?? Math.ceil((data?.Meta?.totalCount || 1) / (data?.Meta?.pageSize || 10));

    const pageSizeOption = [10, 20, 50];
    const sortValues = getSortValues(TEAM_ACCORDION_MEMBERS_HEADINGS);

    const handleOpenDialog = (userid: string) => {
      setIsDialogOpen(true);
      setOnDeleteConfirm(() => () => {
        deleteTeamUserById(teamMeta.id, userid)
          .then(() => {
            refreshMembersList();
            refreshTeamsSummary();
            triggerSnackbar(removeMemberSuccessMessage, NotificationType.Success);
          })
          .catch((axiosError: unknown) => {
            triggerSnackbarError(axiosError as AxiosError<Api.Error>);
          })
          .finally(() => itemsAwaitingDeleteDispatch({ type: 'deleteStateRemove', payload: userid }));
        itemsAwaitingDeleteDispatch({ type: 'deleteStateAdd', payload: userid });
        setIsDialogOpen(false);
      });
    };

    const tableData =
      (!!data && getTableData(data?.Users ?? [], teamMemberDetailsPage, handleOpenDialog, itemsAwaitingDelete)) || {};
    const dataOrder = Object.keys(tableData) ?? [];
    const selected = data?.Users?.flatMap(user => (user.totalOverdue ? [user.userId] : [])) ?? [];

    const debouncedPhraseChange = useDebouncedCallback(phrase => {
      dispatch({ type: 'phraseChange', payload: { phrase } });
    }, SEARCH_DEBOUNCE_TIME);

    const handlePageSizeChange = (pageSize: number) => dispatch({ type: 'pageSizeChange', payload: { pageSize } });
    const handlePageChange = (page: number) => dispatch({ type: 'pageChange', payload: { page } });
    const handleSearchChange = (phrase: string) => {
      setLocalPhrase(phrase);
    };
    const handleSortChange = (event: SelectChangeEvent) => {
      dispatch({ type: 'sortChange', payload: { sortValue: event.target.value } });
    };

    useEffect(() => {
      if (data?.Meta) {
        handlePageChange(data.Meta.page ?? 1);
        handlePageSizeChange(data.Meta.pageSize ?? tableApiProps.pageSize);
      }
    }, [data?.Users?.length, data?.Meta]);

    useEffect(() => {
      debouncedPhraseChange(localPhrase);
    }, [localPhrase]);

    useEffect(() => {
      if (error && !isCancelError(error)) {
        isError.current = true;
      }
    }, [error]);

    return (
      <>
        <li className={classes(styles.customAccordion, className)}>
          <PureHeading level={HeadingLevel.h3} className={styles.accordionHeader}>
            <button
              onClick={() => onClick(teamMeta.id)}
              id={headerId}
              className={styles.button}
              aria-expanded={isExpanded}
              aria-controls={contentId}
            >
              <span className={styles.headerDesktop}>
                <span className={styles.headerText} data-testid={teamAccordionItemIds.teamName}>
                  {teamMeta.teamPathNames}
                </span>
                <span className={styles.accordionHeaderIconContainer}>
                  <WithDevice desktop>
                    {!!overdue && (
                      <span>
                        <Badge variant="warn" className={styles.overdueBadge}>
                          {`${overdue} `}
                          <Label name={COMMON_LABELS.TRAINING.OVERDUE} />
                        </Badge>
                      </span>
                    )}
                    <span className={styles.membersCount} data-testid={teamAccordionItemIds.membersCount}>
                      {`${membersCount} `} <Label name={TEAM_MANAGMENT_LABELS.accordionItem.MEMBERS_COUNT} />
                    </span>
                  </WithDevice>
                  <span className={styles.icon}>{isExpanded ? <AccordionMinusIcon /> : <AccordionPlusIcon />}</span>
                </span>
              </span>
              <WithDevice tablet mobile>
                <span className={styles.headerMobile}>
                  <span className={styles.membersCount} data-testid={teamAccordionItemIds.membersCount}>
                    {`${membersCount} `} <Label name={TEAM_MANAGMENT_LABELS.accordionItem.MEMBERS_COUNT} />
                  </span>
                  {!!overdue && (
                    <span>
                      <Badge variant="warn" className={styles.overdueBadge}>
                        {`${overdue} `}
                        <Label name={COMMON_LABELS.TRAINING.OVERDUE} />
                      </Badge>
                    </span>
                  )}
                </span>
              </WithDevice>
            </button>
          </PureHeading>
          <div>
            <Collapse
              in={isExpanded}
              unmountOnExit
              onExited={() => {
                isError.current = false;
              }}
              id={contentId}
              aria-labelledby={headerId}
            >
              <div className={styles.content}>
                {isError.current ? (
                  <ErrorInfo error={error} internal />
                ) : (
                  <>
                    <TeamAccordionItemActions teamMeta={teamMeta} onUserModification={() => refreshMembersList()} />
                    <Table
                      showPageSizeSelector
                      className={styles.teamManagmentTable}
                      data={tableData}
                      {...tableApiProps}
                      handleItemsOnPage={handlePageSizeChange}
                      handlePageChange={handlePageChange}
                      handleSortChange={handleSortChange}
                      sortValues={sortValues}
                      activeSortValue={activeSortValue}
                      totalPages={totalPages}
                      activePage={tableApiProps.page || 1}
                      pageSizeOptions={pageSizeOption}
                      search={{
                        phrase: tableApiProps.search,
                        handleChange: handleSearchChange,
                        placeholderKey: COMMON_LABELS.SEARCH.TEAM_MEMBER,
                      }}
                      headings={TEAM_ACCORDION_MEMBERS_HEADINGS}
                      dataOrder={dataOrder}
                      selected={selected}
                      isInitialLoad={isNil(data)}
                      isLoading={isNil(data) ? false : isValidating}
                      emptyPlaceholdersCount={2}
                      disableBlurring
                    />
                  </>
                )}
              </div>
            </Collapse>
          </div>
        </li>
        <ConfirmDialog
          onCancel={() => setIsDialogOpen(false)}
          onConfirm={() => onDeleteConfirm()}
          isOpen={isDialogOpen}
          labelKeys={{
            confirmButton: TEAM_MANAGMENT_LABELS.removeMemberDialog.CONFIRM_BUTTON,
            content: TEAM_MANAGMENT_LABELS.removeMemberDialog.CONTENT_TITLE,
            dialogTitle: TEAM_MANAGMENT_LABELS.removeMemberDialog.DIALOG_TITLE,
          }}
        />
      </>
    );
  },
  (prevProps, nextProps) =>
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.membersCount === nextProps.membersCount &&
    prevProps.teamMeta.teamPathNames === nextProps.teamMeta.teamPathNames,
);

export default TeamAccordionItem;
